import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useSettings } from '@/data/Settings';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { transactionsBanksPromotion } from 'integration/generated/transactions';
import { RequestParams } from 'integration/generated/transactions/http-client';
import getValueByPathFromUnknown from 'integration/utils/getValueByPathFromUnknown';
import valueIsString from 'integration/utils/typeAssertions/valueIsString';
import { useCallback } from 'react';
import useSWR from 'swr';

type GenericQuery = Record<string, unknown>;

const bankPromotionApplyPromotionFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		data: { identifier: string },
		query: GenericQuery = {},
		params: RequestParams
	) => {
		const res = await transactionsBanksPromotion(pub).applyPromotion(storeId, data, query, params);
		return res;
	};

const bankPromotionUpdateEligibilityFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		data: { enabled: boolean },
		query: GenericQuery = {},
		params: RequestParams
	) => {
		const res = await transactionsBanksPromotion(pub).updateEligibility(
			storeId,
			data,
			query,
			params
		);
		return res;
	};

const bankPromotionCheckEligibilityFetcher =
	(pub: boolean) =>
	async (storeId: string, query: GenericQuery = {}, params: RequestParams) => {
		const res = await transactionsBanksPromotion(pub).checkEligibility(storeId, query, params);
		return res;
	};

const DATA_KEY_BANK_PROMOTION_CHECK = 'bankPromotionCheckEligibility';

export const useBankPromotionCheckEligibility = (args: { bin?: string; enabled: boolean }) => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const query = useSWR(
		storeId && args.enabled ? [{ storeId, bin: args?.bin }, DATA_KEY_BANK_PROMOTION_CHECK] : null,
		async ([{ storeId, bin }]) =>
			bankPromotionCheckEligibilityFetcher(true)(storeId, { bin }, params)
	);

	return query;
};

export const useApplyBankPromotion = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const onApplyBankPromotion = useCallback(
		async (data: { identifier: string }) => {
			try {
				const res = await bankPromotionApplyPromotionFetcher(true)(storeId, data, {}, params);
				return res;
			} catch (error) {
				const errorMessage = getValueByPathFromUnknown(error, [
					'error',
					'errors',
					0,
					'errorMessage',
				]);

				return {
					success: false,
					message: valueIsString(errorMessage) ? errorMessage : 'Error applying bank promotion',
				};
			}
		},
		[storeId, params]
	);

	return { onApplyBankPromotion };
};

export const useUpdateEligibilityBankPromotion = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const onUpdateEligibilityBankPromotion = useCallback(
		async (data: { enabled: boolean }) => {
			try {
				const res = await bankPromotionUpdateEligibilityFetcher(true)(storeId, data, {}, params);
				return res;
			} catch (error) {
				const errorMessage = getValueByPathFromUnknown(error, [
					'error',
					'errors',
					0,
					'errorMessage',
				]);

				return {
					success: false,
					message: valueIsString(errorMessage)
						? errorMessage
						: 'Error updating bank promotion eligibility',
				};
			}
		},
		[storeId, params]
	);

	return { onUpdateEligibilityBankPromotion };
};

const BANK_PROMOTION_LOCAL_STORAGE_KEY = 'bank_promo';

export const getBankPromotionFromLocalStorage = () => {
	if (typeof window === 'undefined') {
		return null;
	}

	const bankIdentifier = localStorage.getItem(BANK_PROMOTION_LOCAL_STORAGE_KEY);

	if (!valueIsString(bankIdentifier)) {
		return null;
	}

	const bankPromotion = bankIdentifier.replace(/"/g, '');

	return bankPromotion;
};

export const removeBankPromotionFromLocalStorage = () => {
	if (typeof window === 'undefined') {
		return;
	}

	localStorage.removeItem(BANK_PROMOTION_LOCAL_STORAGE_KEY);
};

export const BANK_PROMOTION_SUCCESS_PATHNAME = '/banco-azteca-coupon-access';

export const useApplyBankPromotionInAuth = () => {
	const { onApplyBankPromotion } = useApplyBankPromotion();

	const onApplyBankPromotionInAuth = useCallback(async () => {
		const bankPromotion = getBankPromotionFromLocalStorage();

		if (bankPromotion) {
			const response = await onApplyBankPromotion({
				identifier: bankPromotion,
			});

			removeBankPromotionFromLocalStorage();

			return {
				success: response.success,
				message: response.message,
				redirect: response.success ? BANK_PROMOTION_SUCCESS_PATHNAME : undefined,
			};
		}

		return {
			success: false,
			message: undefined,
			redirect: undefined,
		};
	}, [onApplyBankPromotion]);

	return { onApplyBankPromotionInAuth };
};

export type BankPromotionConfig = {
	active: boolean;
	bank: string;
	deferred: string;
	eligible: string;
	identifier: string;
	promotionName: string;
	redeemed: string;
	minAmount: number;
};

export const INITIAL_BANK_PROMOTION_CONFIG: BankPromotionConfig = {
	active: false,
	bank: '',
	deferred: '',
	eligible: '',
	identifier: '',
	promotionName: '',
	redeemed: '',
	minAmount: 0,
};

export enum BANK_PROMOTION_STATUS {
	ELIGIBLE = 'ELIGIBLE',
	DEFERRED = 'DEFERRED',
	REDEEMED = 'REDEEMED',
}
